/**
 * toast.ts – thin wrapper around sonner so we keep a single place to swap the
 * notification library later, and so i18n keys stay out of call sites.
 */
import { toast } from "sonner";

type Tone = "success" | "error" | "warning" | "info";

/** Short Vietnamese prefix for each tone (kept inline; not localised for speed). */
const TONE_PREFIX: Record<Tone, string> = {
  success: "✓",
  error: "✕",
  warning: "⚠",
  info: "ℹ",
};

/** Fire a toast and also log to console so background/error paths are traceable. */
export function notify(message: string, tone: Tone = "info", opts?: { description?: string; duration?: number }) {
  const prefix = TONE_PREFIX[tone];
  const title = `${prefix} ${message}`;
  const duration = opts?.duration ?? (tone === "error" ? 8000 : 4500);
  switch (tone) {
    case "success":
      toast.success(title, { description: opts?.description, duration });
      break;
    case "error":
      toast.error(title, { description: opts?.description, duration });
      console.error(`[notify:error] ${message}`, opts?.description ?? "");
      break;
    case "warning":
      toast.warning(title, { description: opts?.description, duration });
      console.warn(`[notify:warn] ${message}`, opts?.description ?? "");
      break;
    case "info":
    default:
      toast.info(title, { description: opts?.description, duration });
      break;
  }
}

export const toastSuccess = (m: string, d?: string) => notify(m, "success", { description: d });
export const toastError = (m: string, d?: string) => notify(m, "error", { description: d });
export const toastWarning = (m: string, d?: string) => notify(m, "warning", { description: d });
export const toastInfo = (m: string, d?: string) => notify(m, "info", { description: d });

/** Promise wrapper: shows a loading toast while the promise is pending and a
 * success/error toast when it resolves. Returns the promise so callers can await. */
export function withToast<T>(
  promise: Promise<T>,
  opts: { loading: string; success: string; error: string }
): Promise<T> {
  toast.promise(promise, {
    loading: opts.loading,
    success: opts.success,
    error: (e) => `${opts.error}: ${e instanceof Error ? e.message : String(e)}`,
  });
  return promise;
}
